import useSWR from "swr";
import { APIError } from "../../data/api";
import {
  FeeLine,
  fromOrderItemInput,
  OrderableType,
  Quote,
  QuoteInput,
  toOrderItem,
  toQuoteItem,
  toQuoteRecurrence,
} from "../api";
import { getQuoteIDFromSession } from "@/lib/js_utils/session_storage_utils";
import { useMemo } from "react";
import { hasSmartPricingSearchParam } from "@/offering_v2/utils/has_smart_pricing_search_param";
import { useI18n } from "@/lib/i18n/i18n_provider";

/**
 * useQuoteQuery to improve the api by using: useSWR and useMemo to cache data response
 * by using this we don't recall the api if the input params is not changed
 */
export function useQuoteQuery(quoteInput: QuoteInput | null | undefined) {
  const { locale } = useI18n();
  const bodyParams = useMemo(
    () =>
      quoteInput
        ? fromQuoteInput({
            ...quoteInput,
            quoteId: getQuoteIDFromSession(
              "spaceID" in quoteInput.orderItems?.[0]?.details
                ? quoteInput.orderItems[0].details.spaceID
                : quoteInput.orderItems[0].details.membershipID
            ),
          })
        : null,
    [quoteInput]
  );

  const queryParams = useMemo(() => {
    const searchParams = new URLSearchParams();
    searchParams.set("locale", locale.languageTag);
    if (hasSmartPricingSearchParam()) {
      searchParams.set("smart_pricing", "true");
    }
    return `?${searchParams.toString()}`;
  }, [locale.languageTag]);

  const response = useSWR(
    bodyParams ? [JSON.stringify(bodyParams), queryParams] : null,
    fetchQuote,
    {
      suspense: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  if (!quoteInput)
    return {
      data: undefined,
      error: null,
      errorCode: undefined,
      loading: false,
    };

  return {
    data: response.data ? toQuote(response.data) : undefined,
    error: response.error?.message,
    // The machine code a refusal rode in on (e.g. the recurring family), so
    // callers branch on a token instead of matching the English sentence.
    errorCode:
      response.error instanceof APIError ? response.error.code : undefined,
    loading: (!response.error && !response.data) || response.isValidating,
  };
}

async function fetchQuote(input: any, searchQuery: string) {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: input,
  };

  const response = await fetch("/api/v2/quotes/" + searchQuery, requestOptions);
  const body = await response.json();
  if (response.status >= 200 && response.status < 300) {
    return body;
  }

  throw new APIError(body.message || body.errors?.[0], body.code ?? undefined);
}

export function fromQuoteInput(input: QuoteInput) {
  return {
    source: input.source,
    partner_id: input.partnerID,
    promotion_code: input.promoCode,
    order_items: input.orderItems.map(fromOrderItemInput),
    email: input.email,
    quote_id: input.quoteId,
    partner_offering_id: input.partnerOfferingID,
  };
}

export function toQuote(data: any): Quote {
  const isMembership = data.quote_items.some(
    (quoteItem: any) => quoteItem.orderable_type === OrderableType.Membership
  );
  return {
    id: data.id,
    totalPrice: data.total_price,
    subTotal: data.sub_total,
    subTotalBeforeDiscount: data.sub_total_before_discount,
    available: data.available,
    orderItems: isMembership
      ? data.quote_items.map(toOrderItem)
      : data.order_items.map(toOrderItem),
    quoteItems: data.quote_items.map(toQuoteItem),
    paymentIntent: (data.payment_intent
      ? {
          currency: data.payment_intent?.currency,
          paymentId: data.payment_intent?.payment_id,
          proratedAmount: data.payment_intent?.prorated_amount,
          stripeCustomerId: data.payment_intent?.stripe_customer_id,
          paymentClientSecret: data.payment_intent?.payment_client_secret,
          stripeSubsciptionId: data.payment_intent?.stripe_subsciption_id,
          firstPaymentAmount: data.payment_intent?.first_payment_amount,
        }
      : undefined) as any,
    taxLine: data.tax_line
      ? {
          amount: data.tax_line.amount,
          description: data.tax_line.description,
          percentage: data.tax_line.percentage,
        }
      : null,
    promotionLine: data.promotion_line
      ? {
          amount: data.promotion_line.amount,
          description: data.promotion_line.description,
          code: data.promotion_line.code,
          discount: data.promotion_line.discount,
          discountType: data.promotion_line.discount_type,
          itemPriceAfterDiscount: data.promotion_line.item_price_after_discount,
          subscriptionIterations: data.promotion_line.subscription_iterations,
          subscriptionPromotionEndsAt: data.promotion_line
            .subscription_promotion_ends_at
            ? data.promotion_line.subscription_promotion_ends_at * 1000
            : undefined,
          subscriptionDiscountDuration:
            data.promotion_line.subscription_discount_duration ?? undefined,
          sponsorPayment: data.promotion_line.sponsor_payment
            ? {
                enabled: data.promotion_line.sponsor_payment.enabled,
                message: data.promotion_line.sponsor_payment.message,
              }
            : undefined,
        }
      : null,
    paymentClientSecret: data.payment_client_secret,
    currency: data.currency,
    feeLines: data.fee_lines?.map(toFeeLine),
    passPaymentMethodToPms: data.pass_payment_method_to_pms,
    requestToBook: data.request_to_book,
    recurrence: toQuoteRecurrence(data.recurrence),
  };
}

export function toFeeLine(data: any): FeeLine {
  return {
    id: data.id,
    calculatedAmount: data.calculated_amount,
    displayName: data.display_name,
    displayInformation: data.display_information,
  };
}
